// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { useLayoutEffect } from 'react';
import { useSubMenuState } from '@front/sharedcomponents/ContextMenu/useSubMenuState';
import { useScrollable } from '../Scroll/usePopoverScrollable';
const SUBMENU_OVERLAP = 1;
export function useContextMenu({ menuRef, menuProps, openedMenuRefs }) {
    useScrollable(menuRef, true, 'y');
    const { subMenuState, openSubMenu, closeSubmenu } = useSubMenuState({
        menuRef,
        items: menuProps.items,
        openedMenuRefs,
        hasParent: menuProps.hasParent
    });
    // Adjust menu position so it stays within the viewport.
    // Runs after every render (before paint) to correct position even if React resets inline styles.
    useLayoutEffect(() => {
        const el = menuRef.current;
        if (el === null)
            return;
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (rect.bottom > vh) {
            el.style.top = `${Math.max(0, vh - rect.height)}px`;
        }
        if (rect.right > vw) {
            el.style.left = `${Math.max(0, vw - rect.width)}px`;
        }
        if (rect.left < 0) {
            el.style.left = '0px';
        }
    });
    const onItemPointerEnter = (item, element) => {
        var _a, _b, _c;
        if (isValidArray(item.subitems)) {
            const itemRect = element.getBoundingClientRect();
            const menuRect = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const vw = window.innerWidth;
            // Estimate submenu width using parent menu width (similar items structure).
            const estimatedWidth = (_b = menuRect === null || menuRect === void 0 ? void 0 : menuRect.width) !== null && _b !== void 0 ? _b : 200;
            // Prefer opening to the right; if not enough space, open to the left of the parent menu.
            let x;
            const rightX = itemRect.right - SUBMENU_OVERLAP;
            const leftX = ((_c = menuRect === null || menuRect === void 0 ? void 0 : menuRect.left) !== null && _c !== void 0 ? _c : 0) - estimatedWidth + SUBMENU_OVERLAP;
            if (rightX + estimatedWidth <= vw) {
                x = rightX;
            }
            else if (leftX >= 0) {
                x = leftX;
            }
            else {
                x = Math.max(0, vw - estimatedWidth);
            }
            openSubMenu(item, { x, y: itemRect.top });
        }
        else {
            closeSubmenu();
        }
    };
    const hasLeftOffset = menuProps.items.some((item) => {
        if (item.type !== 'item') {
            return false;
        }
        // Has icon
        if (isNullOrUndefined(item.icon) === false) {
            return true;
        }
        // Has checked state
        if (isNullOrUndefined(item.checked) === false) {
            return true;
        }
        // Has disabled reason
        if (item.disabled === true && isValidString(item.disabledReason)) {
            return true;
        }
        return false;
    });
    const hasRightOffset = menuProps.items.some((item) => item.type === 'item' && isValidArray(item.subitems));
    return {
        subMenuState,
        hasLeftOffset,
        hasRightOffset,
        onItemPointerEnter,
        closeSubmenu
    };
}
